import z from "zod";

// Credential custody, not protocol: oauth is always per member, static_token
// is shared by the organization, none is a public server.
export const McpAuthKindSchema = z.enum(["none", "oauth", "static_token"]);
export type McpAuthKind = z.infer<typeof McpAuthKindSchema>;

// readOnly is the resolved classification (the server's hint, the catalog's
// list, or the admin's mark); readOnlyLocked is set when that mark is the
// hint's or the catalog's, which an admin cannot unmark.
export const McpToolSchema = z.object({
  name: z.string(),
  description: z.string(),
  readOnly: z.boolean(),
  readOnlyLocked: z.boolean().optional(),
  selected: z.boolean(),
});
export type McpTool = z.infer<typeof McpToolSchema>;

// Always connected for servers that need no grant; for oauth servers, whether
// the caller's own grant exists and when it broke.
export const McpConnectionSchema = z.object({
  connected: z.boolean(),
  brokenAt: z.string().nullish(),
});

// Secrets are write-only: the token and client secret never come back.
// Empty strings stand for "none" on the optional string fields. connection is
// only present on project-scoped routes, where the caller's grant is readable.
export const McpConfigSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  iconUrl: z.string(),
  serverUrl: z.string(),
  catalogSlug: z.string(),
  authKind: McpAuthKindSchema,
  enabled: z.boolean(),
  authBrokenAt: z.string().nullish(),
  replacesBuiltin: z.string(),
  builtinChoice: z.enum(["builtin", "mcp"]),
  tools: z
    .array(McpToolSchema)
    .nullable()
    .transform((v) => v ?? []),
  toolsRefreshedAt: z.string().nullish(),
  projects: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  connection: McpConnectionSchema.nullish(),
});
export type McpConfig = z.infer<typeof McpConfigSchema>;

export const McpConfigsResponseSchema = z.object({
  integrations: z
    .array(McpConfigSchema)
    .nullable()
    .transform((v) => v ?? []),
});

// servers are callable as the caller in this project; unconnected are deployed
// oauth servers the caller has not connected to yet.
export const McpProjectResponseSchema = z.object({
  servers: z
    .array(McpConfigSchema)
    .nullable()
    .transform((v) => v ?? []),
  unconnected: z
    .array(McpConfigSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type McpProjectResponse = z.infer<typeof McpProjectResponseSchema>;

// needsCredentials is true when the server is oauth and none of the automatic
// registration methods apply, so the admin has to paste a client id and secret.
export const McpProbeResultSchema = z.object({
  authKind: McpAuthKindSchema,
  needsCredentials: z.boolean(),
});
export type McpProbeResult = z.infer<typeof McpProbeResultSchema>;

export const CreateMcpConfigInputSchema = z.object({
  serverUrl: z.string(),
  name: z.string().optional(),
  slug: z.string().optional(),
  catalogSlug: z.string().optional(),
  iconUrl: z.string().optional(),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
  staticToken: z.string().optional(),
  tokenHeader: z.string().optional(),
  projectIds: z.array(z.string()).optional(),
});
export type CreateMcpConfigInput = z.infer<typeof CreateMcpConfigInputSchema>;

export const ProbeMcpServerInputSchema = z.object({
  serverUrl: z.string(),
});

// Absent fields are left as they are.
export const UpdateMcpConfigInputSchema = z.object({
  name: z.string().optional(),
  builtinChoice: z.enum(["builtin", "mcp"]).optional(),
  tools: z
    .array(z.object({ name: z.string(), selected: z.boolean().optional(), readOnly: z.boolean().optional() }))
    .optional(),
});
export type UpdateMcpConfigInput = z.infer<typeof UpdateMcpConfigInputSchema>;

export const McpConfigParamsSchema = z.object({
  integrationId: z.string(),
});

export const McpConfigProjectParamsSchema = z.object({
  integrationId: z.string(),
  projectId: z.string(),
});

export const McpMembershipsResponseSchema = z.object({
  projects: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
});

export const McpConnectParamsSchema = z.object({
  integrationId: z.string(),
  returnUrl: z.string(),
});

export const McpConnectResponseSchema = z.object({
  authUrl: z.string(),
});

// health is null until the weekly probe has reached the entry. replacesBuiltin
// names the first-party integration the entry stands in for.
export const McpCatalogEntrySchema = z.object({
  slug: z.string(),
  name: z.string(),
  logo: z.string(),
  url: z.string(),
  authKind: McpAuthKindSchema,
  replacesBuiltin: z.string().nullish(),
  health: z.object({ ok: z.boolean() }).nullable(),
});
export type McpCatalogEntry = z.infer<typeof McpCatalogEntrySchema>;

export const McpCatalogResponseSchema = z.object({
  entries: z
    .array(McpCatalogEntrySchema)
    .nullable()
    .transform((v) => v ?? []),
});
